/**
 * @file SubscriptionPlansCard.tsx
 * @brief Super Admin → Billing & Pricing → Plans & seats: the four launch
 *        plans as cards (Free, Pro, Business, Enterprise) + shared settings
 *
 * @details
 * DES 22-10-2026: launch plans are exactly Free, Pro, Business, Enterprise;
 * GB pays in GBP, everyone else in USD. The cards come from
 * GET /api/admin/plans (BACKEND B3 c906e6f): plain labels, the value billing
 * reads now, and the setting key. Save sends only the changed keys to
 * PUT /api/admin/settings and the page lists old → new first, the same rule
 * as the 21-10-2026 plan prices fix. Form model and the diff: launchPlans.ts.
 *
 * Layout (W8, DES 22-10-2026, "looks mixed up"): four cards side by side on
 * wide screens, stacked on narrow; status pill and the price in big type
 * (£ and $ on one line) with one Monthly | Yearly toggle for the page; short
 * groups "Price", "What's included", "Pay as you go", one value per row with
 * its unit; keys only in a hover tooltip. Rows edit in place, "Edit plan"
 * opens a side sheet; changed rows get a dot; one bar at the bottom saves.
 * Shared settings sit in a fifth card, "All plans".
 *
 * Compare rows (W18, DES 24-10-2026): Free, Pro and Business list the same
 * "What's included" and "Pay as you go" rows in the same order
 * (../planCompare.ts); a plan without something says so. A row edits its
 * setting where one exists (Notes fair use from Pro and Business too, same
 * key as "All plans"); the editor is per card, the value per key.
 *
 * Gone with B3: Starter, the Notes 300/600/1200 section and its switch,
 * EUR columns and speaker fees. Self-serve, Notes pool and search switches
 * still come from GET /api/admin/settings; Blocked countries keeps its own
 * API and Save (BlockedCountriesSection.tsx).
 *
 * @author DES
 * @date 25-07-2026
 * @updated 21-10-2026 — per-currency prices, Save sends changed keys only
 * @updated 22-10-2026 — allowed countries → Blocked countries (W3)
 * @updated 22-10-2026 — four launch plan cards from GET /api/admin/plans (W6)
 * @updated 22-10-2026 — cards side by side, rows, in-place edit, changes bar (W8)
 * @updated 24-10-2026 — same rows on Free, Pro and Business (W18)
 * @updated 24-10-2026 — B20 / W17: Free monthly or one time + expiry, every
 *   plan number on its card, "Applies / Affects" before Save
 * @updated 24-10-2026 — W19 (B23): Free "Pay as you go" edits Meeting Notes
 *   after the free minutes + the minimum per Notes recording
 */

import { useEffect, useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { SkeletonFields } from "@/components/ui/Skeleton";
import { TooltipProvider } from "@/components/ui/Tooltip";
import {
  GridBand,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { cn } from "@/shared/theme/cn";
import { useAdminPlans, usePlanImpacts, useSystemSettings, useUpdateSettings, type PlanImpactState } from "../api";
import { BILLING_SECTIONS } from "../billingNav";
import type { PlanCard } from "../schemas";
import { BlockedCountriesSection } from "./BlockedCountriesSection";
import { FreeGrantsSection } from "./FreeGrantsSection";
import { PlanChangesBar, type PlanChangeDetail } from "./PlanChangesBar";
import { FREE_TOGETHER_NOTE, FreeModeControls } from "./FreeModeControls";
import { PlanEditSheet } from "./PlanEditSheet";
import {
  PlanSettingRow,
  PlanSwitchRow,
  ReadOnlyRow,
} from "./PlanSettingRow";
import type { PlanForm } from "../planForm";
import {
  freeOn,
  hasCompareRows,
  includedRows,
  paygRows,
  type CompareContext,
  type CompareRow,
} from "../planCompare";
import {
  affectsText,
  appliesText,
  directionOf,
  extraImpact,
  impactsPending,
  type ImpactView,
} from "../planChangeImpact";
import {
  CURRENCY_REGION,
  CURRENCY_SYMBOL,
  FREE_EXPIRY_KEY,
  FREE_MODE_NUMBER_KEYS,
  FREE_RESET_MODE_KEY,
  PLAN_CURRENCIES,
  changedPlanSettings,
  freeModeOf,
  notesPaygSettings,
  planRowGroup,
  settingsToForm,
  type FormValues,
  type PlanCurrency,
  type PlanSetting,
} from "../launchPlans";

const PLAN_BLURB: Record<PlanCard["code"], string> = {
  // B43 (31-10-2026): never charged per use; interpretation keeps its own rates.
  free: "No card. Free minutes for Meeting Notes or Full Intelligence, never charged. Interpretation is pay as you go after its free minutes.",
  pro: "One person. Billed monthly or yearly.",
  business: "Per seat, pooled across the company.",
  enterprise:
    "Nothing to edit here. Each company's seats, pool and its own Meeting Intelligence and Notes rates are set on Edit Company → Enterprise.",
};

/** Switches that live in GET /api/admin/settings, not on a plan card. */
const OTHER_SWITCHES: { key: string; label: string; hint: string; fallback: boolean }[] = [
  {
    key: "SUBSCRIPTION_ENABLED",
    label: "Plans on sale",
    hint: "When off, no one can buy Pro or Business: pay as you go only.",
    fallback: true,
  },
  {
    key: "NOTES_POOL_ENABLED",
    label: "Separate Meeting Notes pool",
    hint: "Meeting Notes stop using plan minutes; the fair use applies.",
    fallback: false,
  },
  {
    key: "SEARCH_ENABLED",
    label: "Ask AI on",
    hint: "Off = no indexing and no questions.",
    fallback: false,
  },
  {
    key: "SEARCH_ENFORCE_LIMITS",
    label: "Enforce Ask AI limits",
    hint: "When off, questions are counted but never blocked.",
    fallback: false,
  },
];

/** The plain line under a switch on a card (DES 22-10-2026, W8). */
const SWITCH_HINTS: Record<string, string> = {
  FREE_PLAN_ENABLED: "New users can record without a card.",
  FREE_INTERPRET_ENABLED: "New users get the one-time interpretation minutes below.",
  // B43 (31-10-2026)
  FREE_DEVICE_CHECK_ENABLED:
    "Free minutes once per phone or Mac (and no disposable emails). Off: once per account only.",
  PLAN_OVERAGE_ENABLED:
    "Off: no extra Full Intelligence minutes past the plan; new recordings wait for the next plan month. Enterprise unchanged.",
  SUBSCRIPTION_ANNUAL_ENABLED: "Pro and Business can also be bought for a year.",
  ...Object.fromEntries(OTHER_SWITCHES.map((sw) => [sw.key, sw.hint])),
};

type Interval = "month" | "year";

/** The Free keys a reset-mode change keeps together in one Save (B20). */
const FREE_TOGETHER_KEYS = [FREE_RESET_MODE_KEY, ...FREE_MODE_NUMBER_KEYS, FREE_EXPIRY_KEY];

function impactView(st: PlanImpactState | undefined): ImpactView | undefined {
  if (!st) return undefined;
  if (st.status === "ok") return { status: "ok", counts: st.data.counts, note: st.data.note };
  return st;
}

/**
 * "Pro price a month" → "Price a month" inside the Pro card. Switches keep
 * the plan name ("Free plan on").
 */
function cardSettings(card: PlanCard): PlanSetting[] {
  const prefix = `${card.name} p`;
  return card.settings.map((s) =>
    s.kind !== "switch" && s.label.startsWith(prefix)
      ? { ...s, label: `P${s.label.slice(prefix.length)}` }
      : s,
  );
}

function GroupTitle({ children }: { children: string }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/85">
      {children}
    </p>
  );
}

function CurrencyCaption({ cur }: { cur: PlanCurrency }) {
  return (
    <p className="pb-0.5 pl-3.5 pt-2 text-xs text-muted-foreground/80">
      {CURRENCY_REGION[cur]} · {cur} {CURRENCY_SYMBOL[cur]}
    </p>
  );
}

/** Fully round, fill only (DES 03-10 / 05-10-2026). */
function StatusPill({ live, children }: { live: boolean; children: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold",
        live
          ? "bg-success-soft text-success"
          : "bg-black/[0.04] text-muted-foreground dark:bg-white/[0.06]",
      )}
    >
      {live ? <span aria-hidden className="size-1.5 rounded-full bg-success" /> : null}
      {children}
    </span>
  );
}

function IntervalToggle({ value, onChange }: { value: Interval; onChange: (v: Interval) => void }) {
  return (
    <div
      role="radiogroup"
      aria-label="Show prices"
      className="inline-flex rounded-full bg-black/[0.04] p-1 dark:bg-white/[0.06]"
    >
      {(["month", "year"] as const).map((v) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={value === v}
          onClick={() => onChange(v)}
          className={cn(
            "rounded-full px-4 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none",
            value === v
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {v === "month" ? "Monthly" : "Yearly"}
        </button>
      ))}
    </div>
  );
}

export function SubscriptionPlansCard() {
  const plansQuery = useAdminPlans();
  const settingsQuery = useSystemSettings();
  const update = useUpdateSettings();
  const data = plansQuery.data;
  const stored = settingsQuery.data?.settings;

  // W19 (B23): Free's pay-as-you-go Meeting Notes rate + minimum per
  // recording, made from the stored rows and the card's rates.
  const notesPayg = useMemo(() => {
    const free = data?.plans.find((c) => c.code === "free");
    if (!free) return [];
    const rule = data?.shared.settings.find((x) => x.key === "RATE_BOARDROOM_PER_MINUTE_GBP_MINOR")?.rule;
    return notesPaygSettings(free, stored, rule);
  }, [data, stored]);

  // Every editable setting with the group its change line names.
  const entries = useMemo(() => {
    if (!data) return [];
    const out: { setting: PlanSetting; group: string }[] = [];
    for (const card of data.plans) {
      for (const setting of cardSettings(card)) out.push({ setting, group: card.name });
      if (card.code === "free") for (const setting of notesPayg) out.push({ setting, group: card.name });
    }
    for (const setting of data.shared.settings)
      out.push({ setting, group: "All plans" });
    for (const sw of OTHER_SWITCHES) {
      const raw = stored?.[sw.key]?.value;
      const on = raw == null || raw === "" ? sw.fallback : String(raw).toLowerCase() === "true";
      out.push({
        setting: {
          key: sw.key,
          label: sw.label,
          kind: "switch",
          value: raw == null ? null : String(raw),
          effective: on,
        },
        group: "Switches",
      });
    }
    return out;
  }, [data, stored, notesPayg]);
  const byKey = useMemo(
    () => new Map(entries.map((e) => [e.setting.key, e.setting])),
    [entries],
  );

  const baseline = useMemo(
    () => settingsToForm(entries.map((e) => e.setting)),
    [entries],
  );
  const [values, setValues] = useState<FormValues>(baseline);
  useEffect(() => setValues(baseline), [baseline]);

  const changes = useMemo(
    () => changedPlanSettings(entries, baseline, values),
    [entries, baseline, values],
  );
  const changedCount = changes.lines.length;

  const form: PlanForm = useMemo(
    () => ({
      value: (key) => values[key] ?? "",
      set: (key, v) => setValues((prev) => ({ ...prev, [key]: v })),
      baseline,
      invalid: new Set(changes.invalid),
    }),
    [values, baseline, changes.invalid],
  );

  // The review list (B20): the counts are taken while it is open.
  const [reviewOpen, setReviewOpen] = useState(false);
  const toCount = useMemo(
    () => changes.lines.filter((l) => !l.implied && l.stored != null).map((l) => ({ key: l.key, value: l.stored! })),
    [changes.lines],
  );
  const impacts = usePlanImpacts(toCount, reviewOpen);
  const freeModeAfter = freeModeOf(values[FREE_RESET_MODE_KEY] ?? baseline[FREE_RESET_MODE_KEY]);
  const details = useMemo(() => {
    const out: Record<string, PlanChangeDetail> = {};
    const notesChange = changes.settings.FREE_PLAN_NOTES_MINUTES;
    const modeSwitch = FREE_RESET_MODE_KEY in changes.settings;
    for (const l of changes.lines) {
      if (l.implied) continue;
      const setting = byKey.get(l.key);
      const rule = setting?.rule ?? null;
      const view = impactView(impacts[l.key]);
      const before = setting?.value ?? (setting ? String(setting.effective ?? "") : null);
      out[l.key] = {
        applies: appliesText(l.key, rule, directionOf(l.key, before, l.stored), freeModeAfter, modeSwitch),
        affects: affectsText(l.key, view),
        extra: extraImpact(l.key, view, {
          freeMode: freeModeAfter,
          notesView: impactView(impacts.FREE_PLAN_NOTES_MINUTES),
          freeTotal: notesChange ?? null,
          modeSwitch,
        }),
        rule: rule?.affects,
      };
    }
    return out;
  }, [changes, byKey, impacts, freeModeAfter]);
  const pending = reviewOpen && impactsPending(toCount.map((c) => impactView(impacts[c.key])));
  const modeChanging = changes.lines.some((l) => l.key === FREE_RESET_MODE_KEY && !l.implied);
  const together = modeChanging
    ? {
        keys: changes.lines.filter((l) => FREE_TOGETHER_KEYS.includes(l.key)).map((l) => l.key),
        caption: `Free, in one Save. ${FREE_TOGETHER_NOTE.replace(/^Save the mode and the Free numbers together: /, "The ")}`,
      }
    : null;

  const [interval, setInterval] = useState<Interval>("month");
  const [editing, setEditing] = useState<string | null>(null);
  const [sheetFor, setSheetFor] = useState<PlanCard["code"] | null>(null);

  const [ignored, setIgnored] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 4000);
    return () => window.clearTimeout(t);
  }, [saved]);

  const onSave = () => {
    if (changes.invalid.length || changedCount === 0) return;
    setIgnored([]);
    setEditing(null);
    update.mutate(
      { settings: changes.settings },
      {
        onSuccess: (res) => {
          const keys = (res as { ignoredKeys?: unknown })?.ignoredKeys;
          const left = Array.isArray(keys) ? keys.map(String) : [];
          setIgnored(left);
          setSaved(left.length === 0);
        },
      },
    );
  };

  // One setting may sit on several cards (Notes fair use, the no-plan
  // rates), so the open editor is per card: `where:key`.
  const editRow = (where: string, s: PlanSetting, label?: string, valueText?: string, sub?: string) => {
    const id = `${where}:${s.key}`;
    return (
      <PlanSettingRow
        key={id}
        s={s}
        form={form}
        editing={editing === id}
        onEdit={() => setEditing(id)}
        onClose={() => setEditing((k) => (k === id ? null : k))}
        label={label}
        valueText={valueText}
        sub={sub}
      />
    );
  };
  const row = (s: PlanSetting) => editRow("shared", s);

  const ctx: CompareContext = {
    setting: (key) => byKey.get(key),
    value: form.value,
  };
  const compareRow = (where: string, r: CompareRow) =>
    r.unused ? (
      <ReadOnlyRow key={`${where}:${r.id}`} label={r.label} value={r.value} tip={r.tip ?? ""} dim />
    ) : r.setting && r.setting.kind !== "other" ? (
      editRow(where, r.setting, r.label, r.value, r.sub)
    ) : (
      <ReadOnlyRow key={`${where}:${r.id}`} label={r.label} value={r.value} tip={r.tip ?? ""} />
    );

  /** Pay-as-you-go rows, United Kingdom first, then everyone else. */
  const byCurrency = (list: PlanSetting[]) =>
    PLAN_CURRENCIES.map((cur) => {
      const mine = list.filter((s) => s.currency === cur);
      return mine.length ? (
        <div key={cur}>
          <CurrencyCaption cur={cur} />
          {mine.map(row)}
        </div>
      ) : null;
    });

  const bigPrice = (card: PlanCard, settings: PlanSetting[]) => {
    if (card.contactSales) {
      return { amount: "Contact sales", unit: "Priced per company" };
    }
    const prices = settings.filter((s) => planRowGroup(s) === "price" && s.interval === interval);
    if (!prices.length) {
      return { amount: `${CURRENCY_SYMBOL.GBP}0 · ${CURRENCY_SYMBOL.USD}0`, unit: `/ ${interval}` };
    }
    const amount = PLAN_CURRENCIES.flatMap((cur) => {
      const s = prices.find((p) => p.currency === cur);
      return s ? [`${CURRENCY_SYMBOL[cur]}${form.value(s.key).trim() || "–"}`] : [];
    }).join(" · ");
    const seat = card.code === "business" ? "per seat " : "";
    return { amount, unit: `${seat}/ ${interval}` };
  };

  const renderCard = (card: PlanCard) => {
    const settings = cardSettings(card);
    const switches = settings.filter((s) => planRowGroup(s) === "switch");
    const prices = settings.filter((s) => planRowGroup(s) === "price" && s.interval === interval);
    const compare = hasCompareRows(card);
    const included = compare
      ? includedRows(card, ctx).map((r) => compareRow(card.code, r))
      : settings.filter((s) => planRowGroup(s) === "included").map((s) => editRow(card.code, s));
    const payg = compare
      ? PLAN_CURRENCIES.map((cur) => (
          <div key={cur}>
            <CurrencyCaption cur={cur} />
            {paygRows(card, cur, ctx).map((r) => compareRow(`${card.code}:${cur}`, r))}
          </div>
        ))
      : [];
    const price = bigPrice(card, settings);
    const freeOn = card.code === "free" ? form.value("FREE_PLAN_ENABLED") === "true" : card.enabled;
    const pill =
      card.code === "enterprise" ? (
        <StatusPill live={false}>Set per company</StatusPill>
      ) : card.code === "free" ? (
        <StatusPill live={freeOn}>{freeOn ? "On" : "Off"}</StatusPill>
      ) : (
        <StatusPill live={card.enabled}>{card.enabled ? "Live" : "Off"}</StatusPill>
      );
    const changedHere = [...settings, ...(card.code === "free" ? notesPayg : [])].filter(
      (s) => form.value(s.key) !== (baseline[s.key] ?? ""),
    ).length;

    return (
      // Five sections on a shared row grid (subgrid), so "What's included"
      // and "Pay as you go" start at the same height on every card (W18).
      <GridCell key={card.code} className="row-span-5 grid grid-rows-subgrid gap-y-5">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-base font-semibold">{card.name}</p>
            {pill}
          </div>
          <div>
            <p className="text-2xl font-semibold tracking-tight tabular-nums">{price.amount}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{price.unit}</p>
          </div>
          <p className="text-xs text-muted-foreground">{PLAN_BLURB[card.code]}</p>
        </div>

        <div>
          {switches.length ? (
            <div className="-my-2">
              {switches.map((s) => (
                <PlanSwitchRow key={s.key} s={s} form={form} hint={SWITCH_HINTS[s.key]} />
              ))}
              {card.code === "free" ? (
                <FreeModeControls setting={(k) => settings.find((x) => x.key === k)} form={form} />
              ) : null}
            </div>
          ) : null}
          {prices.length ? (
            <>
              <GroupTitle>Price</GroupTitle>
              <div className="mt-1">{prices.map((s) => editRow(card.code, s))}</div>
            </>
          ) : null}
        </div>

        <div>
          {included.length ? (
            <>
              <GroupTitle>What's included</GroupTitle>
              <div className="mt-1">{included}</div>
            </>
          ) : null}
        </div>

        <div>
          {payg.length ? (
            <>
              <GroupTitle>Pay as you go</GroupTitle>
              {payg}
            </>
          ) : null}
        </div>

        {card.contactSales ? (
          <div />
        ) : (
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setSheetFor(card.code);
            }}
            className="inline-flex items-center justify-center gap-2 self-end justify-self-start rounded-full bg-black/[0.04] px-4 py-2 text-sm font-medium transition-colors hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/[0.1]"
          >
            <Pencil className="size-3.5" aria-hidden />
            Edit plan
            {changedHere ? (
              <span className="text-xs text-muted-foreground">· {changedHere} changed</span>
            ) : null}
          </button>
        )}
      </GridCell>
    );
  };

  const renderShared = () => {
    if (!data) return null;
    const shared = data.shared.settings;
    const switches = shared.filter((s) => planRowGroup(s) === "switch");
    const included = shared.filter((s) => planRowGroup(s) === "included");
    const payg = shared.filter((s) => planRowGroup(s) === "payg");
    const noPlanUnused = freeOn(ctx);
    return (
      <GridCell className="flex flex-col gap-5">
        <div>
          <p className="text-base font-semibold">All plans</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Settings every plan shares.
          </p>
        </div>
        {switches.length ? (
          <div className="-my-2">
            {switches.map((s) => (
              <PlanSwitchRow key={s.key} s={s} form={form} hint={SWITCH_HINTS[s.key]} />
            ))}
          </div>
        ) : null}
        {included.length ? (
          <div>
            <GroupTitle>Limits</GroupTitle>
            <div className="mt-1">{included.map(row)}</div>
          </div>
        ) : null}
        {payg.length ? (
          <div>
            <GroupTitle>No plan · pay as you go</GroupTitle>
            {/* W34: B43 made a no-plan personal account Free, never charged.
                BACKEND still reads these when Free is off and for a
                company-billed recording with no company rate, so they stay
                editable, greyed. */}
            {noPlanUnused ? (
              <p className="pl-3.5 pt-1 text-xs text-muted-foreground">
                Not used: Free is never charged (B43). Only used if Free is switched off, or for a company-billed
                recording with no company rate.
              </p>
            ) : null}
            <div className={noPlanUnused ? "opacity-60" : undefined}>{byCurrency(payg)}</div>
          </div>
        ) : null}
      </GridCell>
    );
  };

  const busy = plansQuery.isLoading || settingsQuery.isLoading;
  const sheetCard = data?.plans.find((c) => c.code === sheetFor) ?? null;
  const yearlyOff = interval === "year" && form.value("SUBSCRIPTION_ANNUAL_ENABLED") === "false";

  return (
    <TooltipProvider delayDuration={200}>
      <GridBand
        id={BILLING_SECTIONS.subscriptionPlans}
        className="flex flex-wrap items-end justify-between gap-3 bg-muted/25 py-2.5"
      >
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Plans
          </p>
          <h3 className="mt-0.5 text-base font-semibold tracking-tight">
            Plans & seats
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            The United Kingdom pays in GBP, everyone else in USD. Click a value
            to change it; nothing is saved until you review and save.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <IntervalToggle value={interval} onChange={setInterval} />
          {yearlyOff ? (
            <p className="text-xs text-muted-foreground">Yearly plans are off: no one can buy these.</p>
          ) : null}
        </div>
      </GridBand>

      {busy ? (
        <GridCell>
          <SkeletonFields fields={4} />
        </GridCell>
      ) : plansQuery.isError || !data ? (
        <GridCell>
          <p className="text-sm text-destructive">
            Couldn't load the plans.
            {plansQuery.error ? ` ${plansQuery.error.message}` : ""}
          </p>
        </GridCell>
      ) : (
        <>
          <GridCells className="grid-cols-1 md:grid-cols-2 min-[1360px]:grid-cols-4">
            {data.plans.map(renderCard)}
          </GridCells>

          <GridCells className="grid-cols-1 md:grid-cols-2">
            {renderShared()}
            <GridCell className="flex flex-col gap-3">
              <div>
                <p className="text-base font-semibold">Switches</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Selling plans, the Meeting Notes pool and Ask AI.
                </p>
              </div>
              <div>
                {OTHER_SWITCHES.map((sw) => {
                  const s = byKey.get(sw.key);
                  return s ? (
                    <PlanSwitchRow key={sw.key} s={s} form={form} hint={sw.hint} />
                  ) : null;
                })}
              </div>
            </GridCell>
          </GridCells>
        </>
      )}

      <BlockedCountriesSection />

      {/* B43: free minutes once per account and per device. */}
      <FreeGrantsSection />

      <PlanChangesBar
        lines={changes.lines}
        invalidCount={changes.invalid.length}
        saving={update.isPending}
        saved={saved && changedCount === 0}
        error={update.isError ? update.error.message : null}
        ignored={ignored}
        onSave={onSave}
        onUndo={(key) => {
          // Undoing the Free mode puts its numbers back too: they belong together (B20).
          const keys = key === FREE_RESET_MODE_KEY ? FREE_TOGETHER_KEYS : [key];
          setValues((prev) => ({ ...prev, ...Object.fromEntries(keys.filter((k) => k in baseline).map((k) => [k, baseline[k]])) }));
        }}
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        details={details}
        pending={pending}
        together={together}
        onUndoAll={() => {
          setEditing(null);
          setValues(baseline);
        }}
      />

      {sheetCard ? (
        <PlanEditSheet
          open={sheetFor !== null}
          onOpenChange={(o) => {
            if (!o) setSheetFor(null);
          }}
          title={sheetCard.name}
          settings={sheetCard.code === "free" ? [...cardSettings(sheetCard), ...notesPayg] : cardSettings(sheetCard)}
          form={form}
          switchHints={SWITCH_HINTS}
        />
      ) : null}
    </TooltipProvider>
  );
}
